/**
 * /akun/pengaturan — Settings (Phase 5.4; dirapikan 2026-09-09).
 *
 * Isi setelah pembersihan (aksesibilitas, notifikasi, dan ekspor CSV
 * dihapus atas keputusan user — tidak pernah diminta & CSV tidak berfungsi):
 *  - Bahasa (BI only Y1; EN scaffolded)
 *  - Tema tampilan (system / light; dark Y2)
 *  - Ubah PIN (navigates to /akun/ubah-pin)
 *  - Hapus PIN (Lupa PIN flow → /akun/hapus-data)
 *
 * Settings persisted via PUT /v1/settings/:child_id (session-gated).
 */

import { useCallback, useEffect, useState } from 'react';
import { useRouter } from 'expo-router';
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { ThemedText } from '@/components/themed-text';
import { ThemedView } from '@/components/themed-view';
import { Brand, Radii, Spacing } from '@/constants/theme';
import { getActiveChild } from '@/lib/api/children';
import { getSettings, updateSettings, type AppSettings } from '@/lib/api/parent';
import { loadSession, clearSession } from '@/lib/parent-session';
import { ApiError } from '@/lib/api/client';

import type { ChildProfile } from '@funpaper/shared';

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; child: ChildProfile; sessionToken: string; settings: AppSettings }
  | { kind: 'error'; message: string };

export default function PengaturanScreen() {
  const router = useRouter();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  // --------------------------------------------------------------------------
  // Load
  // --------------------------------------------------------------------------

  useEffect(() => {
    (async () => {
      const session = await loadSession();
      if (!session) {
        router.replace('/akun/pin' as never);
        return;
      }
      try {
        const child = await getActiveChild();
        if (!child) {
          setState({ kind: 'error', message: 'Belum ada profil anak.' });
          return;
        }
        const result = await getSettings(child.id, session.token);
        if (!result.ok) {
          setState({ kind: 'error', message: result.message ?? 'Gagal memuat pengaturan.' });
          return;
        }
        setState({ kind: 'ready', child, sessionToken: session.token, settings: result.settings });
        setSettings(result.settings);
      } catch (err) {
        const message = err instanceof ApiError ? err.message : 'Gagal memuat pengaturan.';
        setState({ kind: 'error', message });
      }
    })();
  }, [router]);

  // --------------------------------------------------------------------------
  // Update handler (used by all toggles)
  // --------------------------------------------------------------------------

  const onUpdate = useCallback(
    async (patch: Partial<AppSettings>) => {
      if (state.kind !== 'ready' || !settings) return;
      const next = { ...settings, ...patch };
      setSettings(next); // optimistic
      setSaving(true);
      try {
        const result = await updateSettings(state.child.id, state.sessionToken, patch);
        if (!result.ok) {
          // Roll back.
          setSettings(settings);
          setToast(result.message ?? 'Gagal menyimpan');
        }
      } catch (err) {
        setSettings(settings);
        const message = err instanceof ApiError ? err.message : 'Gagal menyimpan pengaturan.';
        setToast(message);
      } finally {
        setSaving(false);
      }
    },
    [state, settings],
  );

  // --------------------------------------------------------------------------
  // Lock zone
  // --------------------------------------------------------------------------

  const onLockZone = useCallback(async () => {
    await clearSession();
    router.replace('/akun' as never);
  }, [router]);

  // --------------------------------------------------------------------------
  // Render
  // --------------------------------------------------------------------------

  if (state.kind === 'loading') {
    return (
      <ThemedView style={styles.root}>
        <SafeAreaView style={styles.safe} edges={['top', 'left', 'right']}>
          <View style={styles.center}>
            <ActivityIndicator size="large" color={Brand.primary} />
          </View>
        </SafeAreaView>
      </ThemedView>
    );
  }

  if (state.kind === 'error' || !settings) {
    return (
      <ThemedView style={styles.root}>
        <SafeAreaView style={styles.safe} edges={['top', 'left', 'right']}>
          <View style={styles.center}>
            <ThemedText type="subtitle" style={styles.errorTitle}>
              {state.kind === 'error' ? state.message : 'Gagal memuat'}
            </ThemedText>
            <Pressable onPress={() => router.back()} style={styles.retryBtn}>
              <Text style={styles.retryText}>Kembali</Text>
            </Pressable>
          </View>
        </SafeAreaView>
      </ThemedView>
    );
  }

  return (
    <ThemedView style={styles.root}>
      <SafeAreaView style={styles.safe} edges={['top', 'left', 'right']}>
        <View style={styles.header}>
          <Pressable
            onPress={() => router.back()}
            style={({ pressed }) => [styles.backBtn, pressed && styles.btnPressed]}
            accessibilityRole="button"
            accessibilityLabel="Kembali"
          >
            <Text style={styles.backArrow}>←</Text>
          </Pressable>
          <ThemedText type="subtitle" style={styles.headerTitle}>Pengaturan</ThemedText>
          <View style={{ width: 40 }} />
        </View>

        <ScrollView contentContainerStyle={styles.scrollContent}>
          {/* Bahasa */}
          <Section title="Bahasa & Tampilan">
            <Picker
              label="Bahasa"
              value={settings.language === 'bi' ? 'Bahasa Indonesia' : 'English'}
              disabled
            />
            <Picker
              label="Tema tampilan"
              value={settings.theme === 'system' ? 'Otomatis (sistem)' : settings.theme === 'light' ? 'Terang' : 'Gelap'}
              disabled
            />
          </Section>

          {/* PIN & Data */}
          <Section title="PIN & Data">
            <ActionRow
              label="Ubah PIN"
              subtitle="Ganti PIN Profil dan Preferensi"
              onPress={() => router.push('/akun/ubah-pin' as never)}
            />
            <ActionRow
              label="Hapus PIN"
              subtitle="Lupa PIN? Hapus semua data + PIN untuk mulai ulang"
              onPress={() => router.push('/akun/hapus-data' as never)}
              destructive
            />
          </Section>

          {toast ? <Text style={styles.toastText}>{toast}</Text> : null}

          <Pressable
            onPress={onLockZone}
            style={({ pressed }) => [styles.lockBtn, pressed && styles.btnPressed]}
            accessibilityRole="button"
            accessibilityLabel="Kunci Profil dan Preferensi dan kembali ke Akun"
          >
            <Text style={styles.lockBtnText}>Kunci Profil dan Preferensi</Text>
          </Pressable>
        </ScrollView>
      </SafeAreaView>
    </ThemedView>
  );
}

// ----------------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------------

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>{title}</Text>
      <View style={styles.sectionCard}>{children}</View>
    </View>
  );
}

function Picker({ label, value, disabled }: { label: string; value: string; disabled?: boolean }) {
  return (
    <View style={styles.row}>
      <View style={styles.rowContent}>
        <ThemedText type="smallBold" style={styles.rowLabel}>{label}</ThemedText>
        <ThemedText type="small" themeColor="textSecondary">{value}</ThemedText>
      </View>
      <Text style={[styles.pickerValue, disabled && styles.pickerDisabled]}>
        {disabled ? null : '›'}
      </Text>
    </View>
  );
}

function ActionRow({
  label,
  subtitle,
  onPress,
  destructive,
}: {
  label: string;
  subtitle: string;
  onPress: () => void;
  destructive?: boolean;
}) {
  return (
    <Pressable
      onPress={onPress}
      style={({ pressed }) => [styles.row, pressed && styles.btnPressed]}
      accessibilityRole="button"
      accessibilityLabel={label}
    >
      <View style={styles.rowContent}>
        <ThemedText
          type="smallBold"
          style={destructive ? styles.rowLabelDestructive : styles.rowLabel}
        >
          {label}
        </ThemedText>
        <ThemedText type="small" themeColor="textSecondary">{subtitle}</ThemedText>
      </View>
      <Text style={styles.chev}>›</Text>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1 },
  safe: { flex: 1 },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: Spacing.three,
    paddingVertical: Spacing.two,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e5e5',
  },
  backBtn: { width: 40, height: 40, alignItems: 'center', justifyContent: 'center' },
  backArrow: { fontSize: 24, color: Brand.primary, fontWeight: '600' },
  headerTitle: { flex: 1, textAlign: 'center', fontSize: 18, fontWeight: '700', color: '#1A0B5C' },
  scrollContent: { padding: Spacing.four, paddingBottom: Spacing.five, gap: Spacing.four },
  section: { gap: Spacing.two },
  sectionTitle: { fontSize: 12, fontWeight: '600', color: '#6b6573', textTransform: 'uppercase', letterSpacing: 0.5 },
  sectionCard: { backgroundColor: '#ffffff', borderRadius: Radii.medium, borderWidth: 1, borderColor: '#e5e5e5', overflow: 'hidden' },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: Spacing.three,
    paddingVertical: Spacing.three,
    borderBottomWidth: 1,
    borderBottomColor: '#f5f3ee',
    gap: Spacing.three,
  },
  rowContent: { flex: 1 },
  rowLabel: { color: '#1A0B5C', fontSize: 14 },
  rowLabelDestructive: { color: '#c2410c', fontSize: 14 },
  pickerValue: { fontSize: 20, color: '#9CA3AF' },
  pickerDisabled: { fontSize: 14 },
  chev: { fontSize: 24, color: '#9CA3AF', fontWeight: '300' },
  toastText: { color: Brand.primary, fontSize: 13, textAlign: 'center' },
  lockBtn: {
    backgroundColor: Brand.primary,
    paddingVertical: 14,
    borderRadius: Radii.medium,
    alignItems: 'center',
    marginTop: Spacing.three,
  },
  lockBtnText: { color: '#ffffff', fontSize: 16, fontWeight: '700' },
  btnPressed: { opacity: 0.7 },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: Spacing.four, gap: Spacing.three },
  errorTitle: { color: Brand.primary, textAlign: 'center' },
  retryBtn: { marginTop: Spacing.two, paddingHorizontal: Spacing.four, paddingVertical: Spacing.two, backgroundColor: Brand.primary, borderRadius: Radii.medium },
  retryText: { color: '#ffffff', fontSize: 14, fontWeight: '600' },
});
